// Type1 Mock Data - Teacher View
// This is for Teacher View (SimpleTimetableGrid)
// School: Hollins - Rows are 20 teachers
// Cell shows: Subject, Class/Set, Room
// Popup shows: Subject and Class (title), Room (and capacity), Period/Lesson Date, Period/Lesson Timing

export interface CellData {
  id: string;
  label: string;
  color: string;
  isUnavailable?: boolean;
  popupData: {
    title: string;
    subtitle?: string;
    details: { label: string; value: string }[];
  };
}

export interface RowData {
  id: string;
  title: string;
  cells: Record<string, CellData | null>; // key: "weekA-monday-1" format
}

export interface Type1Data {
  rows: RowData[];
  weeks: ('A' | 'B')[];
  days: string[];
  periods: number[];
}

// Subject colors - each subject has a unique color
const subjectColors: Record<string, string> = {
  Eng: '#10B981', // Green
  Mat: '#3B82F6', // Blue
  Sci: '#8B5CF6', // Purple
  DT: '#F59E0B',  // Amber
  His: '#EF4444', // Red
  MFL: '#0EA5E9', // Sky
  Geo: '#EC4899', // Pink
  Art: '#6366F1', // Indigo
};

// Full subject names
const subjectFullNames: Record<string, string> = {
  Eng: 'English',
  Mat: 'Mathematics',
  Sci: 'Science',
  DT: 'Design Technology',
  His: 'History',
  MFL: 'Modern Foreign Languages',
  Geo: 'Geography',
  Art: 'Art',
};

// Teachers by subject with full names
interface TeacherInfo {
  initials: string;
  fullName: string;
  subject: string;
}

const allTeachers: TeacherInfo[] = [
  // English Teachers
  { initials: 'SHW', fullName: 'Mr S Howard', subject: 'Eng' },
  { initials: 'NTA', fullName: 'Ms N Taylor', subject: 'Eng' },
  { initials: 'SWA', fullName: 'Mrs S Walsh', subject: 'Eng' },
  // Maths Teachers
  { initials: 'JGA', fullName: 'Mr J Garcia', subject: 'Mat' },
  { initials: 'NBR', fullName: 'Mrs N Brown', subject: 'Mat' },
  { initials: 'DHA', fullName: 'Mr D Hall', subject: 'Mat' },
  // Science Teachers
  { initials: 'ECA', fullName: 'Dr E Carter', subject: 'Sci' },
  { initials: 'SBM', fullName: 'Mr S Bloom', subject: 'Sci' },
  // Design Teachers
  { initials: 'SSC', fullName: 'Mr S Scott', subject: 'DT' },
  { initials: 'EBA', fullName: 'Ms E Baker', subject: 'DT' },
  // History Teachers
  { initials: 'CWA', fullName: 'Mr C Watson', subject: 'His' },
  { initials: 'ASA', fullName: 'Ms A Sanders', subject: 'His' },
  // MFL Teachers
  { initials: 'CKI', fullName: 'Mrs C King', subject: 'MFL' },
  { initials: 'JGR', fullName: 'Mr J Green', subject: 'MFL' },
  { initials: 'HDO', fullName: 'Ms H Donovan', subject: 'MFL' },
  // Geography Teachers
  { initials: 'TDU', fullName: 'Mr T Duncan', subject: 'Geo' },
  { initials: 'NJO', fullName: 'Mrs N Jones', subject: 'Geo' },
  { initials: 'HUM', fullName: 'Dr H Humphrey', subject: 'Geo' },
  // Art Teachers
  { initials: 'LKE', fullName: 'Ms L Kelly', subject: 'Art' },
  { initials: 'PDR', fullName: 'Mr P Drew', subject: 'Art' },
  { initials: 'LWH', fullName: 'Mrs L White', subject: 'Art' },
];

// Use 20 teachers (exclude one)
const teachers = allTeachers.slice(0, 20);

// All rooms with capacities
interface RoomInfo {
  name: string;
  capacity: number;
}

const allRooms: RoomInfo[] = [
  { name: 'GYM', capacity: 120 },
  { name: 'Hall', capacity: 200 },
  { name: 'LRC', capacity: 60 },
  ...Array.from({ length: 30 }, (_, i) => ({
    name: `R${i + 1}`,
    capacity: 25 + Math.floor(Math.random() * 15), // Random capacity between 25-40
  })),
];

// Subjects array
const subjects = Object.keys(subjectColors);

// Year groups and their sets
const yearGroups = [7, 8, 9, 10, 11];
const bandXSets789 = ['H', 'O', 'L'];
const bandYSets789 = ['I', 'N', 'S'];
const bandXSets1011 = ['H', 'O', 'L', 'T'];
const bandYSets1011 = ['I', 'N', 'S', 'C'];

// Period times
const periodTimes = [
  { start: '09.05', end: '10.05' },
  { start: '10.05', end: '11.05' },
  { start: '11.20', end: '12.20' },
  { start: '13.00', end: '14.00' },
  { start: '14.00', end: '15.00' },
];

// Dates for January 2026 (Mon 5th - Fri 9th for Week A, Mon 12th - Fri 16th for Week B)
const weekDates: Record<string, Record<string, string>> = {
  A: {
    Monday: 'Mon 5th Jan 2026',
    Tuesday: 'Tue 6th Jan 2026',
    Wednesday: 'Wed 7th Jan 2026',
    Thursday: 'Thu 8th Jan 2026',
    Friday: 'Fri 9th Jan 2026',
  },
  B: {
    Monday: 'Mon 12th Jan 2026',
    Tuesday: 'Tue 13th Jan 2026',
    Wednesday: 'Wed 14th Jan 2026',
    Thursday: 'Thu 15th Jan 2026',
    Friday: 'Fri 16th Jan 2026',
  },
};

// Seeded random for consistency
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Helper to create cell key
const cellKey = (week: 'A' | 'B', day: string, period: number) =>
  `week${week}-${day.toLowerCase()}-${period}`;

// Get a set letter based on year group and seed
const getSetLetter = (yearGroup: number, seed: number): string => {
  if (yearGroup <= 9) {
    const allSets = [...bandXSets789, ...bandYSets789];
    return allSets[Math.floor(seededRandom(seed) * allSets.length)];
  } else {
    const allSets = [...bandXSets1011, ...bandYSets1011];
    return allSets[Math.floor(seededRandom(seed) * allSets.length)];
  }
};

// Helper to create a cell for Teacher View
// Cell shows: Subject, Class/Set, Room
// Popup shows: Subject and Class (title), Room (and capacity), Period/Lesson Date, Period/Lesson Timing
const createCell = (
  id: string,
  subject: string,
  yearGroup: number,
  setLetter: string,
  room: RoomInfo,
  week: 'A' | 'B',
  day: string,
  period: number,
  isUnavailable = false
): CellData => {
  const subjectCode = subject;
  const subjectName = subjectFullNames[subject] || subject;
  const color = subjectColors[subject] || '#94A3B8';
  const time = periodTimes[period - 1];
  const date = weekDates[week][day];

  // Cell label for Teacher View: Subject, Class/Set, Room
  const label = `${subjectCode}\n${yearGroup}${setLetter}\n${room.name}`;

  return {
    id,
    label,
    color,
    isUnavailable,
    popupData: {
      title: `${subjectName} ${yearGroup}${setLetter}`,
      subtitle: subjectCode,
      details: [
        { label: 'Room', value: `${room.name} (Capacity: ${room.capacity})` },
        { label: 'Date', value: date },
        { label: 'Time', value: `${time.start} - ${time.end}` },
      ],
    },
  };
};

// Generate rows with sample data
const generateRows = (): RowData[] => {
  const weeks: ('A' | 'B')[] = ['A', 'B'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const periods = [1, 2, 3, 4, 5];

  return teachers.map((teacher, teacherIndex) => {
    const cells: Record<string, CellData | null> = {};

    weeks.forEach((week, weekIndex) => {
      days.forEach((day, dayIndex) => {
        periods.forEach((period) => {
          const key = cellKey(week, day, period);
          const seed = teacherIndex * 1000 + weekIndex * 100 + dayIndex * 10 + period;
          
          // Randomly determine if this cell has a lesson (80% chance)
          if (seededRandom(seed) > 0.2) {
            const yearGroup = yearGroups[Math.floor(seededRandom(seed + 1) * yearGroups.length)];
            const setLetter = getSetLetter(yearGroup, seed + 2);
            const room = allRooms[Math.floor(seededRandom(seed + 3) * allRooms.length)];
            // Use teacher's own subject
            const subject = teacher.subject;

            cells[key] = createCell(
              `${teacher.initials}-${key}`,
              subject,
              yearGroup,
              setLetter,
              room,
              week,
              day,
              period,
              // Mark some cells as unavailable (about 5%)
              seededRandom(seed + 4) < 0.05
            );
          } else {
            cells[key] = null; // Empty cell (free period)
          }
        });
      });
    });

    return {
      id: `row-${teacher.initials}`,
      title: teacher.initials,
      cells,
    };
  });
};

export const type1MockData: Type1Data = {
  rows: generateRows(),
  weeks: ['A', 'B'],
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  periods: [1, 2, 3, 4, 5],
};

export default type1MockData;
